'use strict';

var _ = require('lodash');

/**
 * Formatter Service
 *
 * Formats axis labels
 *
 * @param $filter
 * @param momentService
 * @constructor
 */
var FormatterService = function FormatterService($filter, momentService) {
  var rmNumberFormat = $filter('rmNumberFormat');
  var rmMomentFormat = $filter('rmMomentFormat');

  /**
   *
   * @param xPropertyName {string} property name for data object
   * @param dataFunction {Function} function that returns whole data for chart
   * @param params.yearFormat format used to display year tick
   * @param params.monthFormat format used to display month tick
   * @param params.dayFormat format used to display day tick
   * @param params.dayLimit optional limit after which month tick cannot be shown
   * @param params.ignoreGap flag for ignoring gaps when calculating ticks
   * @returns {Function}
   */
  function commonMomentTick(xPropertyName, dataFunction, params) {
    // keeps the index of a previously generated label
    var lastIndex = 0;
    var searchedObject = {};
    function getDay(data, index) {
      return data[index][xPropertyName].date();
    }
    function getMonth(data, index) {
      return data[index][xPropertyName].month();
    }
    function getYear(data, index) {
      return data[index][xPropertyName].year();
    }
    function showYear(data, i, index, lastIndex) {
      return i === 0 || index === 0 || getYear(data, index) !== getYear(data, lastIndex);
    }
    function showMonth(data, i, index, lastIndex) {
      return i === 0 || index === 0 || getMonth(data, index) !== getMonth(data, lastIndex);
    }

    // i - the D3 index of a bar we are preparing label for
    return function (d, i) {
      // reset lastIndex if we just started returning labels
      if (i === 0) {
        lastIndex = 0;
      }
      var data = dataFunction();
      searchedObject[xPropertyName] = d;
      var index = _.sortedIndexBy(data, searchedObject, xPropertyName);
      if (!params.ignoreGap && data[index].isGap) {
        return undefined;
      } else {
        var isYearShown = showYear(data, i, index, lastIndex);
        var isMonthShown = showMonth(data, i, index, lastIndex);
        isMonthShown = params.dayLimit ? isMonthShown && getDay(data, index) <= params.dayLimit : isMonthShown;
        lastIndex = index;
        if (isYearShown && params.yearFormat) {
          return rmMomentFormat(d, params.yearFormat);
        }
        if (isMonthShown && params.monthFormat) {
          return rmMomentFormat(d, params.monthFormat);
        }
        return params.dayFormat ? rmMomentFormat(d, params.dayFormat) : undefined;
      }
    };
  }
  function commonRunNumberTick(xPropertyName, dataFunction, params) {
    // keeps the index of a previously generated label
    var lastIndex = 0;
    var searchedObject = {};
    function getDay(data, index) {
      return parseInt(data[index][xPropertyName].slice(6, 8));
    }
    function getMonth(data, index) {
      return parseInt(data[index][xPropertyName].slice(4, 6));
    }
    function getYear(data, index) {
      return parseInt(data[index][xPropertyName].slice(0, 4));
    }
    function showYear(data, i, index, lastIndex) {
      return i === 0 || index === 0 || getYear(data, index) !== getYear(data, lastIndex);
    }
    function showMonth(data, i, index, lastIndex) {
      return i === 0 || index === 0 || getMonth(data, index) !== getMonth(data, lastIndex);
    }

    // i - the D3 index of a bar we are preparing label for
    return function (d, i) {
      // reset lastIndex if we just started returning labels
      if (i === 0) {
        lastIndex = 0;
      }
      var data = dataFunction();
      searchedObject[xPropertyName] = d;
      var index = _.sortedIndexBy(data, searchedObject, xPropertyName);
      if (!params.ignoreGap && data[index].isGap) {
        return undefined;
      } else {
        var isYearShown = showYear(data, i, index, lastIndex);
        var isMonthShown = showMonth(data, i, index, lastIndex);
        isMonthShown = params.dayLimit ? isMonthShown && getDay(data, index) <= params.dayLimit : isMonthShown;
        lastIndex = index;
        var runNumberDate = momentService.of(getYear(data, index) + '-' + getMonth(data, index) + '-' + getDay(data, index), momentService.FIELD.OPTIMIZATION_RUN_DATE, momentService.FORMAT.DATE_ISO);
        var runNumberInADay = d.slice(8, 9);
        var dayNum;
        if (isYearShown && params.yearFormat) {
          if (params.runFrequency > 1 && !data[index].isGap) {
            var dayMonthYearLabel = rmMomentFormat(runNumberDate, params.yearFormat).split('|');
            dayNum = dayMonthYearLabel[0];
            var monthYear = dayMonthYearLabel[1];
            return dayNum + '|(' + runNumberInADay + ')|' + monthYear;
          }
          return rmMomentFormat(runNumberDate, params.yearFormat);
        }
        if (isMonthShown && params.monthFormat) {
          if (params.runFrequency > 1 && !data[index].isGap) {
            var dayMonthLabel = rmMomentFormat(runNumberDate, params.monthFormat).split('|');
            dayNum = dayMonthLabel[0];
            var monthNum = dayMonthLabel[1];
            return dayNum + '|(' + runNumberInADay + ')|' + monthNum;
          }
          return rmMomentFormat(runNumberDate, params.monthFormat);
        }
        if (params.dayFormat) {
          if (params.runFrequency > 1 && !data[index].isGap) {
            var dayLabel = rmMomentFormat(runNumberDate, params.dayFormat);
            return dayLabel + '|(' + runNumberInADay + ')';
          }
          return rmMomentFormat(runNumberDate, params.dayFormat);
        }
        return undefined;
      }
    };
  }

  /**
   * Returns day and month at start of the month, day otherwise
   * @param {MomentConstants.FORMATS} monthFormat
   * @param {MomentConstants.FORMATS} dayFormat
   * @param {string} xPropertyName
   * @param {Function} dataFunction
   * @param {boolean} ignoreGap
   * @returns {Function}
   */
  function momentTick(monthFormat, dayFormat, xPropertyName, dataFunction, ignoreGap) {
    return commonMomentTick(xPropertyName, dataFunction, {
      monthFormat: monthFormat,
      dayFormat: dayFormat,
      ignoreGap: ignoreGap
    });
  }

  /**
   * Returns month at start of the month till 25th day of the month
   * @param {MomentConstants.FORMATS} monthFormat
   * @param {string} xPropertyName
   * @param {Function} dataFunction
   * @returns {Function}
   */
  function momentMonthTick(monthFormat, xPropertyName, dataFunction) {
    return commonMomentTick(xPropertyName, dataFunction, {
      monthFormat: monthFormat,
      dayLimit: 25
    });
  }
  function runNumberMonthTick(monthFormat, xPropertyName, dataFunction, runFrequency) {
    return commonRunNumberTick(xPropertyName, dataFunction, {
      monthFormat: monthFormat,
      dayLimit: 25,
      runFrequency: runFrequency
    });
  }

  /**
   * Returns day with month and year at start of year, day and month at start of the month, day otherwise
   * @param {MomentConstants.FORMATS} yearFormat
   * @param {MomentConstants.FORMATS} monthFormat
   * @param {MomentConstants.FORMATS} dayFormat
   * @param {string} xPropertyName
   * @param {Function} dataFunction
   * @param {boolean} ignoreGap
   * @returns {Function}
   */
  function momentWithYearTick(yearFormat, monthFormat, dayFormat, xPropertyName, dataFunction, ignoreGap) {
    return commonMomentTick(xPropertyName, dataFunction, {
      yearFormat: yearFormat,
      monthFormat: monthFormat,
      dayFormat: dayFormat,
      ignoreGap: ignoreGap
    });
  }
  function runNumberWithYearTick(yearFormat, monthFormat, dayFormat, xPropertyName, dataFunction, ignoreGap, runFrequency) {
    return commonRunNumberTick(xPropertyName, dataFunction, {
      yearFormat: yearFormat,
      monthFormat: monthFormat,
      dayFormat: dayFormat,
      ignoreGap: ignoreGap,
      runFrequency: runFrequency
    });
  }

  /**
   * Returns year at the start of a year in data set
   * @param {MomentConstants.FORMATS} yearFormat
   * @param {string} xPropertyName
   * @param {Function} dataFunction
   * @param {boolean} ignoreGap
   * @returns {Function}
   */
  function momentYearTick(yearFormat, xPropertyName, dataFunction) {
    return commonMomentTick(xPropertyName, dataFunction, {
      yearFormat: yearFormat
    });
  }

  /**
   * Returns original value
   * @param d
   * @returns {*}
   */
  function identity(d) {
    return d;
  }
  function returnStackLabelFormatter(dataFunction) {
    return function (d, i) {
      var data = dataFunction();
      return data[i].stackLabel;
    };
  }

  /**
   * Returns human readable date
   * @param d - datum item
   * @returns {string}
   */
  function humanReadable(d) {
    return rmNumberFormat(d, 'NUMBER_SHORT');
  }
  function numberFormatter(d) {
    var result = d % 1 === 0 ? d : d.toFixed(2);
    result = result === '0.00' || result === '-0.00' ? '0' : result;
    return rmNumberFormat(result, 'NUMBER_SHORT');
  }
  return {
    identity: identity,
    numberFormatter: numberFormatter,
    momentTick: momentTick,
    momentMonthTick: momentMonthTick,
    momentWithYearTick: momentWithYearTick,
    momentYearTick: momentYearTick,
    humanReadable: humanReadable,
    returnStackLabelFormatter: returnStackLabelFormatter,
    runNumberMonthTick: runNumberMonthTick,
    runNumberWithYearTick: runNumberWithYearTick
  };
};
FormatterService.$inject = ['$filter', 'MomentService'];
module.exports = FormatterService;